import { CoreContext } from 'context/CoreContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ReadByActionsSince } from 'services/log-cobranca';
import { ReadByPeriod as ReadAttempts } from 'services/tentativa-cobranca';
import { Read as ReadTitulos } from 'services/titulo';
import { exposeStrapiError } from 'utils';
import {
  PERFORMANCE_LOG_ACTIONS,
  buildOperatorPerformance,
  buildPerformanceCards,
  canSeeAllOperators,
  createOperatorResolver,
  currentMonthRange,
  dayEndIso,
  dayStartIso,
  isValidRange,
  listOperators,
  readAllPages,
} from 'utils/billingReports';

const isFailure = (result) => !result || !!result?.error;

const NO_OPERATOR = '-';

// Painel de desempenho por operador do menu Cobranca. Junta tres fontes do
// periodo, todas com o escopo por distribuidor aplicado no backend:
//
// - `GET /tentativa-cobrancas`: o atendimento (tentativas e resultados);
// - `GET /log-cobrancas`: quem renegociou, baixou na tela e encaminhou. Vai do
//   inicio do periodo ate hoje, para achar o cancelamento posterior de uma
//   renegociacao do periodo;
// - `GET /titulos` baixados (pagamentos) a partir do inicio do periodo: o
//   valor das baixas manuais e a conferencia das promessas, que podem vencer
//   depois do fim do periodo.
//
// Sem a flag `desempenho_cobranca`, o painel fica preso nos numeros do
// proprio usuario (ver canSeeAllOperators).
export default function useOperatorPerformance() {
  const { user } = useContext(CoreContext);
  const seeAll = canSeeAllOperators(user);

  const [period, setPeriod] = useState(currentMonthRange);
  const [selectedOperator, setSelectedOperator] = useState('');
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [titulos, setTitulos] = useState([]);

  const { from, to } = period;
  const validRange = isValidRange(period);

  // A chave do operador e o id do usuario (ver createOperatorResolver).
  const keyOf = useMemo(
    () => createOperatorResolver({ attempts, logs, user }),
    [attempts, logs, user]
  );
  // Sem a flag, o recorte e o proprio usuario. Um `user` sem id nem e-mail nao
  // pode cair no recorte vazio, que mostraria todos: vira um valor que nao
  // casa com ninguem.
  const ownKey = keyOf({
    usuario: { id: user?.id },
    usuario_email: user?.email,
  });
  const operator = seeAll ? selectedOperator : ownKey || NO_OPERATOR;

  const reload = useCallback(async () => {
    if (!isValidRange({ from, to })) {
      return;
    }
    setLoading(true);
    try {
      const [attemptItems, logItems, tituloItems] = await Promise.all([
        readAllPages(
          (page) => ReadAttempts(dayStartIso(from), dayEndIso(to), page),
          exposeStrapiError
        ),
        readAllPages(
          (page) =>
            ReadByActionsSince(
              PERFORMANCE_LOG_ACTIONS,
              dayStartIso(from),
              page
            ),
          isFailure
        ),
        readAllPages(
          (page) =>
            ReadTitulos(
              `?filters[status][$eq]=baixado&filters[tipo_baixa][$eq]=normal&filters[data_baixa][$gte]=${from}&pagination[page]=${page}&pagination[pageSize]=1000`
            ),
          isFailure
        ),
      ]);
      if (!logItems || !tituloItems) {
        toast.warning(
          'Parte dos indicadores não pôde ser carregada: renegociações, baixas ou promessas podem aparecer zeradas.'
        );
      }
      setAttempts(attemptItems || []);
      setLogs(logItems || []);
      setTitulos(tituloItems || []);
    } catch (err) {
      console.error('Erro ao carregar o desempenho:', err);
      toast.error('Erro ao carregar o desempenho dos operadores');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    reload();
  }, [reload]);

  const performance = useMemo(
    () =>
      buildOperatorPerformance({
        attempts,
        logs,
        titulos,
        period,
        operator,
        keyOf,
      }),
    [attempts, logs, titulos, period, operator, keyOf]
  );

  const operators = useMemo(
    () => (seeAll ? listOperators({ attempts, logs, keyOf }) : []),
    [seeAll, attempts, logs, keyOf]
  );

  const cards = useMemo(
    () => buildPerformanceCards(performance.rows),
    [performance.rows]
  );

  return {
    loading,
    period,
    setPeriod,
    validRange,
    seeAll,
    operators,
    selectedOperator,
    setSelectedOperator,
    ...performance,
    cards,
    reload,
  };
}
